import React, { Fragment } from 'react';
import { G } from '@react-pdf/renderer';
import { deviceIcons, MicrophoneTile } from '../../globalConstants/Icons/Report';
import { clamp, has, isArray, isNull, values } from 'lodash';
import { MIC_CATEGORIES, micInCategory } from '../../types/models/microphone';

// BMA360D ceiling-tile rendering — mirrors the live-app MuiDevices (elements/). The
// tile body is drawn at the size of the physical ceiling tile the mic occupies, so
// its icon scale is derived from the footprint rather than the fixed icon size used
// for every other (point-located) device. Body units (47) match the tile body rect
// in Report.js's MicrophoneTile; the inner mic is kept near normal size via the
// inverse innerScale. Footprints (metres) mirror the makeBma360d() values in the CRD
// constants — kept here so the PDF renderer stays free of the tool-config import.
const CEILING_TILE_ICON_BODY_UNITS = 47;
const CEILING_TILE_INNER_MIC_SCALE = 0.7;
const PDF_CEILING_TILE_FOOTPRINT: Record<string, number> = {
  bma360d_24: 0.6096, // imperial: 24"×24" tile
  bma360d_600: 0.6,   // metric: 600mm×600mm tile
};

// Translation from product config keys (what the calc puts on each layout entry) to
// the legacy icon keys used by Report.js's `microphonePaths`. Without this every mic
// (primary AND expansion) falls through to the grey-circle fallback because the icon
// registry uses 'ceilingmic' / 'pendantmic' / etc. while state carries 'tcm_x' /
// 'tcm_1' / etc. Mirrors the same map in the live-app MuiDevices (elements/) — kept
// separate because the two icon registries use slightly different key spellings
// (legacy 'ceilingmic' here vs. 'ceiling_mic' in the live app).
const PDF_MIC_ICON_MAP: Record<string, string> = {
  tcm_x:       'ceilingmic',
  dcm_x:       'ceilingmic', // ceiling expansion mic — same icon as TCM-X
  tcm_1:       'pendantmic',
  ttm_x:       'tabletopmic',
  vbc_2500a:   'videobar',
  vbc_2800:    'videobar',
  abc_2500a:   'audiobar',
  cbc_2500_cm: 'ceiling_bar',
  cbc_2500_pm: 'ceiling_bar',
  // BMA360D ceiling tiles are NOT mapped here — they render through MicrophoneTile
  // (footprint-scaled tile body), not the circular grille marker. See the tile branch
  // in the render below.
};

/**
 * Interface for device location
 */
interface DeviceLocation {
  x: number;
  y: number;
}

/**
 * Interface for device data
 */
interface Device {
  model: string;
  location: DeviceLocation;
  horAngle?: number;
  [key: string]: any;
}

/**
 * Interface for processed device
 */
interface ProcessedDevice {
  model: string;
  iconModel: string;
  x: number;
  y: number;
  angle: number | null;
  iconRotation: number;
  // Ceiling-tile (BMA360D) mics render a footprint-scaled tile body instead of the
  // fixed-size circular marker, so they carry their own icon scale and inner-mic scale.
  isTile: boolean;
  iconScale: number;
  innerScale: number;
}

/**
 * Interface for area location
 */
interface AreaLocation {
  top: number;
  left: number;
  [key: string]: any;
}

/**
 * Interface for area data
 */
interface Area extends AreaLocation {
  id: string;
  devices: Device[];
  [deviceType: string]: any;
}

/**
 * Interface for processed area
 */
interface ProcessedArea {
  id: string;
  top: number;
  left: number;
  devices: Device[];
  isCBCBar: boolean;
}

/**
 * Interface for areas object
 */
interface Areas {
  [key: string]: Area;
}

/**
 * Interface for content
 */
interface Content {
  areas: Areas;
  [key: string]: any;
}

/**
 * Interface for icon props
 */
interface IconProps {
  model?: string;
  angle?: number;
  [key: string]: any;
}

/**
 * Interface for device icons
 */
interface DeviceIcons {
  [deviceType: string]: React.ComponentType<IconProps>;
}

/**
 * Interface for component props
 */
interface MuiDevicesProps {
  inContainer?: boolean;
  areaId?: string;
  scale: number;
  content: Content;
  device: string;
}

/**
 * MUI Devices component that replaces the styled-components Devices component
 * 
 * This component provides a Material UI equivalent of the original Devices component
 * with similar props and functionality. It's used for PDF generation.
 * 
 * @param {MuiDevicesProps} props
 * @returns {JSX.Element} MUI Devices component
 */
const MuiDevices: React.FC<MuiDevicesProps> = (props) => {
  const { inContainer, areaId, scale, content: { areas }, device } = props;

  const areaArray = (areaId ? [areas?.[areaId]].filter(Boolean) : values(areas)).map((area: Area) => {
    // For cameras, check if camera is 'none' or not enabled
    if (device === 'cameras') {
      const cameras = area[device];
      if (!cameras || !cameras.hasCamera || cameras.model === 'none') {
        // Skip rendering cameras when they're set to 'none' or not enabled
        return {
          id: area.id,
          top: area.location?.top || 0,
          left: area.location?.left || 0,
          devices: [],
        };
      }
    }

    // Concat expansion layouts so their icons land on the PDF canvas. The expansion
    // entries carry the same `model` / `location` shape as primary devices (set by the
    // calc and the addExpansion* reducers), so the downstream icon picker and angle
    // logic don't need to branch.
    const primaryLayout = isArray(area[device]) ? area[device] : area[device]?.layout || [];
    let allDevices = primaryLayout;
    if (device === 'microphones') {
      const expansionMics = (area as any).expansionMicrophones?.layout ?? [];
      if (expansionMics.length > 0) allDevices = [...primaryLayout, ...expansionMics];
    } else if (device === 'speakers') {
      const expansionSpkrs = (area as any).expansionSpeakers?.layout ?? [];
      if (expansionSpkrs.length > 0) allDevices = [...primaryLayout, ...expansionSpkrs];
    }

    return {
      id: area.id,
      top: area.location?.top || 0,
      left: area.location?.left || 0,
      devices: allDevices,
      isCBCBar: MIC_CATEGORIES.CBC.has((area.microphones as any)?.model),
    };
  }) as ProcessedArea[];

  const Icon = deviceIcons[device] as React.ComponentType<IconProps> | undefined;
  const Arrow = deviceIcons.deviceArrow as React.ComponentType<IconProps>;
  const iconSize = 0.005;

  if (!Icon) return null;
  // Fixed visual size for point-located devices (the marker does not grow with the room).
  const baseIconScale = clamp(iconSize * scale, 0.13, 0.90);

  const devices: ProcessedDevice[] = areaArray.flatMap(area => {
    const x0 = 1 + (inContainer ? area.left * scale : 0);
    const y0 = 1 + (inContainer ? area.top * scale : 0);
    // Check if area.devices exists and is an array
    if (!area.devices || !Array.isArray(area.devices)) {
      return [];
    }
    return area.devices.map((dev: Device) => {
      // Ensure device has location data
      const location = dev.location || { x: 0, y: 0 };
      let iconRotation = 0;
      let angle: number | null = null;
      if (has(dev, 'horAngle')) {
        const horAngle = dev.horAngle ?? 0;
        if (device === 'cameras') {
          const isBarCamera = dev.model === 'barCamera' || dev.model === 'barCamera2800';
          iconRotation = isBarCamera
            ? (horAngle + 90) % 360
            : horAngle % 360;
          angle = horAngle;
        } else if (device === 'microphones' && micInCategory(dev.model, 'BAR')) {
          // CBC bars are ceiling-mounted: horizontal at horAngle=0 → +180°
          // VBC/ABC bars are wall-mounted: vertical at horAngle=0 → +270°
          iconRotation = area.isCBCBar ? (horAngle + 180) % 360 : (horAngle + 270) % 360;
          // CBC bars don't get the directional arrow overlay (the ceiling_bar icon shape
          // already conveys orientation); VBC/ABC wall bars keep it.
          angle = area.isCBCBar ? null : horAngle;
        } else if (device === 'speakers' && dev.model === 'barspkr') {
          // CBC bar speakers are ceiling-mounted: horizontal at horAngle=0 → +180°
          iconRotation = area.isCBCBar ? (horAngle + 180) % 360 : (horAngle + 270) % 360;
          // No arrow overlay for CBC bar speakers either — matches the mic (see above).
          angle = area.isCBCBar ? null : horAngle;
        } else if (device === 'speakers' && dev.model.startsWith('wallspkr')) {
          // Wall speakers: horAngle uses 0=right/90=down/180=left/270=up convention,
          // which matches the speaker icon's default orientation (cone points right at 0°).
          iconRotation = horAngle % 360;
          angle = horAngle;
        } else {
          // Ceiling/pendant speakers always point down into the room
          iconRotation = 90;
          angle = horAngle;
        }
      }
      // CBC bar mic uses the ceiling-mount `ceiling_bar` icon (PIG-138); its bar speaker uses
      // the same `ceiling_bar` icon so the two render identically. Other mics translate through
      // PDF_MIC_ICON_MAP so 'tcm_x' / 'ttm_x' / etc. resolve to the legacy icon keys Report.js
      // expects. Speakers and cameras already use registry-matching keys (ceilingspkr / barspkr /
      // barCamera), so we pass them through untranslated.
      const isCbcBarMic =
        area.isCBCBar && device === 'microphones' && micInCategory(dev.model, 'BAR');
      const isCbcBarSpkr =
        area.isCBCBar && device === 'speakers' && dev.model === 'barspkr';
      let iconModel: string;
      if (isCbcBarMic || isCbcBarSpkr) {
        iconModel = 'ceiling_bar';
      } else if (device === 'microphones') {
        iconModel = PDF_MIC_ICON_MAP[dev.model] ?? dev.model;
      } else {
        iconModel = dev.model;
      }

      // BMA360D ceiling tiles draw a tile body sized to their physical footprint and
      // centred on the mic location — matching the room design — instead of the fixed
      // circular marker. Scale mirrors the live-app MuiDevices: (footprint * scale) /
      // body units. Falls back to the fixed marker scale if the footprint is unknown.
      const tileFootprint = device === 'microphones' ? PDF_CEILING_TILE_FOOTPRINT[dev.model] : undefined;
      const isTile = Boolean(tileFootprint);
      const iconScale = isTile
        ? (tileFootprint as number) * scale / CEILING_TILE_ICON_BODY_UNITS
        : baseIconScale;
      const innerScale = isTile ? CEILING_TILE_INNER_MIC_SCALE : 1;

      return ({
        model: dev.model,
        iconModel,
        x: x0 + (location.x || 0) * scale,
        y: y0 + (location.y || 0) * scale,
        angle,
        iconRotation,
        isTile,
        iconScale,
        innerScale
      });
    });
  });

  return (
    <Fragment>
      {devices.map((device, index) =>
        <G key={`Icon-${index}`} transform={`translate(${device.x} ${device.y}) scale(${device.iconScale})`} >
          <G transform={`rotate(${device.iconRotation})`}>
            {device.isTile
              ? <MicrophoneTile innerScale={device.innerScale} />
              : <Icon model={device.iconModel} />}
          </G>
          {!isNull(device.angle) && <Arrow angle={device.angle} />}
        </G>
      )}
    </Fragment>
  );
};

export default MuiDevices;
